import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';

import { Authorization } from '../../model/Authorization';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthorizationService, VoidData } from 'src/app/services/authorization/authorization.service';

/** Dialog data provided to Void Authorization Dialog */
export interface VoidAuthorizationDialogData {
  authorization: Authorization;
}

@Component({
    selector: 'app-void-authorization-dialog',
    templateUrl: './void-authorization-dialog.component.html',
    styleUrl: './void-authorization-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VoidAuthorizationDialogComponent {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<VoidAuthorizationDialogComponent> = inject(MatDialogRef);
  private readonly _authorizationService: AuthorizationService = inject(AuthorizationService);
  private readonly _toastrService: ToastrService = inject(ToastrService);

  /** Dialog data */
  public data: VoidAuthorizationDialogData = inject(MAT_DIALOG_DATA);

  /** Dynamic form variables */
  voidForm: FormGroup;
  isSubmitting = false;

  /**
   * On construct, initializes the void form
   */
  constructor() {
    this.voidForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
        Validators.max(this.data.authorization.remainingAmount),
        Validators.pattern(/^\d+(\.\d{1,2})?$/) // Allows numbers with up to 2 decimals
      ]),
      memo: new FormControl('')
    });
  }

  /**
   * Handle void form submission
   */
  public submit(): void {
    // Check if the form is valid
    if (this.voidForm.invalid) {
      this._toastrService.error('Please fill in all required fields correctly');
      return;
    }

    this.isSubmitting = true;

    try {
      // Create void data to send
      const formValues = this.voidForm.value;
      const voidData: VoidData = {
        amount: Number(formValues.amount),
        memo: formValues.memo || '',
      };

      // Submit the void request (using capture service as base - would need specific void service)
      this._authorizationService.voidAuthorization(this.data.authorization.id, voidData)
      .pipe(finalize(() => setTimeout(() => this.isSubmitting = false, 1000)))
      .subscribe({
        next: () => {
          this._toastrService.success('Void sent successfully');
          this.close(true);
        },
        error: (err: any) => {
          console.log('[VOID-AUTHORIZATION-DIALOG-COMPONENT]: could not void authorization', err);
          this._toastrService.error('Failed to send void');
        }
      });
    } catch (err: any) {
      // Catch errors from form validation or transaction service
      console.error('[VOID-AUTHORIZATION-DIALOG-COMPONENT]: Error voiding authorization:', err);
      this._toastrService.error('Failed to send void');
      this.isSubmitting = false;
    }
  }

  /**
   * Close the dialog
   */
  public close(success: boolean = false) {
    this._dialogRef.close({ success });
  }
}
